/**
 * Edge Function URL Routing Utility
 *
 * Returns the base URL for an Edge Function, routing to Cloudflare Workers
 * for functions that have been migrated from Supabase.
 *
 * Phase 99: Deploy Missing Functions to Cloudflare Workers
 */

/**
 * Map of function names to their Workers path.
 * API v1 functions use clean /v1/ paths; other functions keep their name as path.
 */
const WORKERS_FUNCTION_PATHS: Record<string, string> = {
  // API v1 — clean /v1/ paths
  'api-v1-activities': '/v1/activities',
  'api-v1-bulk': '/v1/bulk',
  'api-v1-contacts': '/v1/contacts',
  'api-v1-deals': '/v1/deals',
  'api-v1-organizations': '/v1/organizations',
  'api-v1-tasks': '/v1/tasks',
  'api-v1-webhooks': '/v1/webhooks',
  // Twilio
  'twilio-bundle-webhook': '/twilio-bundle-webhook',
  'twilio-monthly-billing': '/twilio-monthly-billing',
  'twilio-price-debug': '/twilio-price-debug',
  'twilio-provision-number': '/twilio-provision-number',
  'twilio-release-number': '/twilio-release-number',
  'twilio-search-numbers': '/twilio-search-numbers',
  // sevDesk
  'download-invoice-pdf': '/download-invoice-pdf',
  'sync-sevdesk-invoices': '/sync-sevdesk-invoices',
  'test-sevdesk-connection': '/test-sevdesk-connection',
  // Cron/Scheduled
  'calendar-sync': '/calendar-sync',
  'email-sequence-executor': '/email-sequence-executor',
  'send-scheduled-emails': '/send-scheduled-emails',
  // Misc
  'amazon-sync-seller-info': '/amazon-sync-seller-info',
  'backfill-task-calendar-descriptions': '/backfill-task-calendar-descriptions',
  'export-user-data': '/export-user-data',
  // Webhooks
  'webhook-dispatch': '/webhook-dispatch',
  // Google OAuth
  'google-oauth-callback': '/google-oauth-callback',
  'google-calendar-tasks-oauth': '/google-calendar-tasks-oauth',
  'video-conferencing-oauth': '/video-conferencing-oauth',
  // Calendar fetches
  'fetch-google-calendars': '/fetch-google-calendars',
  'fetch-calendar-task-sync-calendars': '/fetch-calendar-task-sync-calendars',
  'fetch-video-conf-calendars': '/fetch-video-conf-calendars',
  // GMaps Genie (Phase 116)
  'gmaps-genie': '/gmaps-genie',
  // Sending Providers (Phase 118)
  'test-sending-provider': '/test-sending-provider',
  'send-email-resend': '/send-email-resend',
  // Content Repurpose (Phase 101)
  'extract-content-source': '/extract-content-source',
  'oembed': '/oembed',
  'generate-social-draft': '/generate-social-draft',
  'social-publish': '/social-publish',
  // Email AI (Compose/Reply/Transform)
  'email-ai-compose': '/email-ai-compose',
  'email-ai-reply': '/email-ai-reply',
  'email-ai-transform': '/email-ai-transform',
  // Review Funnel (Phase 150)
  'generate-review-text': '/generate-review-text',
};

export function getEdgeFunctionUrl(functionName: string): string {
  const workerPath = WORKERS_FUNCTION_PATHS[functionName];
  if (workerPath) {
    const workersUrl = import.meta.env.VITE_WORKERS_URL;
    if (!workersUrl) {
      // Fallback to Supabase if Workers URL not configured (dev mode)
      console.warn(`VITE_WORKERS_URL not set, falling back to Supabase for ${functionName}`);
      return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
    }
    return `${workersUrl}${workerPath}`;
  }
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
}
